import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, BookOpen, Zap, MessageSquare, Key, CreditCard, Settings, FileText, HelpCircle } from "lucide-react";

interface Article {
  id: string;
  title: string;
  description?: string;
  content: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  articles: Article[];
}

// Categories with articles
const CATEGORIES: Category[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    description: "New to Echonour? Start here",
    icon: <Zap className="h-6 w-6" />,
    articles: [
      {
        id: "what-is-echonour",
        title: "What is Echonour?",
        content: `Echonour is an AI-powered transcription service that converts YouTube videos, podcasts, and audio files into accurate transcripts with speaker identification.

Key Features:
- Automatic transcription — Paste a URL and get a full transcript in minutes
- Speaker identification — AI detects and labels who's speaking
- Chat with transcripts — Ask questions and get answers with citations
- Library organization — Folders, favorites, and search across all content
- Export options — Download as TXT, SRT, VTT, or JSON

Who uses Echonour?
- Podcast listeners who want searchable notes
- Researchers analyzing interviews
- Content creators repurposing video content
- Teams documenting meetings and webinars`
      },
      {
        id: "first-transcription",
        title: "Your First Transcription",
        content: `Getting your first transcript is easy — no account required.

Step 1: Paste a URL
On the home page, paste any YouTube URL:
\`https://www.youtube.com/watch?v=example\`

Step 2: Click Transcribe
Hit the Transcribe button and wait 1-3 minutes.

Step 3: Review Results
You'll see:
- Full transcript with speaker labels
- Timestamps you can click to jump around
- AI-generated summary

Step 4: Save (Optional)
Create a free account to:
- Save transcripts to your Library
- Organize with folders
- Chat with your content

Tips:
- Longer videos take slightly more time
- Popular podcasts often have cached transcripts (instant!)
- Try batch mode for multiple videos at once`
      },
      {
        id: "create-account",
        title: "Creating Your Account",
        content: `Creating an account unlocks the full Echonour experience.

What You Get:
- Library to save and organize transcripts
- Folders for different projects
- Chat feature to ask questions
- Export in multiple formats

How to Sign Up:
1. Click "Sign In" in the top right
2. Enter your email address
3. Check your inbox for a magic link
4. Click the link — you're in!

Free Tier Includes:
- 5 hours of transcription
- 20 chat messages per month
- Unlimited library storage

Already have an account?
Click "Sign In" and enter your email to receive a new login link.`
      },
    ],
  },
  {
    id: "transcription",
    name: "Transcription",
    description: "How transcription works",
    icon: <FileText className="h-6 w-6" />,
    articles: [
      {
        id: "how-transcription-works",
        title: "How Transcription Works",
        content: `Echonour uses state-of-the-art AI to convert audio to text.

The Process:
1. Audio Extraction — We download the audio from your source
2. Speech Recognition — AI converts speech to text
3. Speaker Detection — We identify different voices
4. Speaker Naming — AI figures out who's speaking using context
5. Summary Generation — Quick overview of the content

What We Support:
- YouTube videos
- Podcast RSS feeds
- Direct audio uploads (MP3, WAV, M4A, FLAC)
- Most video platforms via yt-dlp

Processing Time:
- Most videos: 1-3 minutes
- Long videos (3+ hours): 5-10 minutes
- Cached content: Instant

Accuracy:
- Speech-to-text: 95%+ for clear audio
- Speaker identification: 85-95% for known speakers`
      },
      {
        id: "supported-platforms-formats",
        title: "Supported Platforms & Formats",
        content: `Echonour supports transcription from a wide range of video platforms, audio sources, and file formats.

Video Platforms:
| Platform | Support | Notes |
|----------|---------|-------|
| YouTube | ✅ Full | Videos, Shorts, live streams |
| Vimeo | ✅ Full | Public videos |
| Twitter/X | ✅ Full | Video posts |
| TikTok | ✅ Full | Public videos |
| Facebook | ⚠️ Partial | Public videos only |
| Instagram | ⚠️ Partial | Public reels/videos |
| TED Talks | ✅ Full | ted.com videos |
| Twitch | ✅ Full | VODs and clips |
| Dailymotion | ✅ Full | Public videos |
| Rumble | ✅ Full | Public videos |
| Odysee | ✅ Full | Public videos |

Podcast Sources:
- Direct RSS feed URLs
- Apple Podcasts links (converted to RSS)
- Spotify podcast links (when RSS available)
- Direct audio file URLs (.mp3, .m4a)

Audio File Uploads:
| Format | Max Size | Notes |
|--------|----------|-------|
| MP3 | 1 GB | Most common, recommended |
| M4A/AAC | 1 GB | Apple/iTunes format |
| WAV | 1 GB | Uncompressed, large files |
| FLAC | 1 GB | Lossless compression |
| OGG | 1 GB | Open source format |
| OPUS | 1 GB | Efficient compression |
| WEBM | 1 GB | Audio extracted automatically |

Audio Requirements:
- Sample rate: 8kHz - 48kHz (16kHz+ recommended)
- Channels: Mono or stereo
- Bitrate: 64kbps minimum (128kbps+ recommended)
- Duration: No maximum (3+ hour podcasts work fine)

Language Support:
Echonour supports 99+ languages including:
- English (US, UK, AU, and more)
- Spanish, French, German, Italian, Portuguese
- Mandarin, Japanese, Korean
- Hindi, Arabic, Hebrew
- Dutch, Polish, Russian, Swedish
- And many more — select in Settings

Export Formats:
| Format | Best For |
|--------|----------|
| TXT | Simple text, notes |
| SRT | Video subtitles, YouTube captions |
| VTT | Web video players |
| JSON | Developers, integrations |

What Doesn't Work:
- Private/unlisted videos (we can't access them)
- DRM-protected content
- Live streams in progress (wait for VOD)
- Geo-restricted content outside your region
- Sites requiring login (Netflix, Hulu, etc.)

Tips for Best Results:
1. Use direct YouTube/video URLs when possible
2. For podcasts, RSS feeds give cleanest audio
3. Upload high-quality audio files (128kbps+)
4. Clear speech with minimal background noise = best accuracy`
      },
      {
        id: "speaker-identification",
        title: "Speaker Identification",
        content: `Echonour automatically identifies who's speaking in your content.

How It Works:
1. AI detects different voices in the audio
2. We analyze video descriptions and titles for names
3. Our database of known speakers helps identify hosts
4. Context clues (introductions, references) refine accuracy

Accuracy Factors:
- Clear audio = better detection
- Known speakers (popular podcasters) = higher accuracy
- Video descriptions with guest names help significantly

Editing Speaker Names:
1. Open any transcript
2. Click on a speaker name
3. Type the correct name
4. All instances update automatically

Building Your Speaker Database:
The more you transcribe from the same sources, the better Echonour gets at identifying recurring speakers.`
      },
      {
        id: "batch-transcription",
        title: "Batch Transcription",
        content: `Process multiple videos at once with batch transcription.

How to Use:
1. Click the "Batch" tab on the home page
2. Paste multiple YouTube URLs (one per line, up to 10)
3. Click "Start Batch"
4. Monitor progress for all items

Features:
- Parallel processing — Multiple videos transcribe simultaneously
- Progress tracking — See status of each item
- Automatic deduplication — Skip already-transcribed videos
- Save all — Add entire batch to a folder with one click

Use Cases:
- Transcribe an entire podcast series
- Process multiple interviews for research
- Build a searchable content library

Tips:
- Cached videos are instant (marked as "cached")
- Total cost shown upfront before starting
- Failed items can be retried individually`
      },
      {
        id: "export-formats",
        title: "Export Formats",
        content: `Download your transcripts in multiple formats.

Available Formats:

TXT (Plain Text)
- Simple text with speaker labels
- Best for: Reading, searching, copying

SRT (SubRip Subtitles)
- Industry-standard subtitle format
- Best for: Video editing software, YouTube captions

VTT (Web Video Text Tracks)
- Web-native subtitle format
- Best for: HTML5 video players, web embedding

JSON (Structured Data)
- Full data with timestamps and metadata
- Best for: Developers, data analysis, integrations

How to Export:
1. Open any transcript
2. Click the Export button (↓)
3. Select your format
4. File downloads automatically`
      },
    ],
  },
  {
    id: "chat",
    name: "Chat Feature",
    description: "Ask questions about your content",
    icon: <MessageSquare className="h-6 w-6" />,
    articles: [
      {
        id: "chat-overview",
        title: "Chat Overview",
        content: `Chat lets you have AI conversations about your transcripts.

What You Can Do:
- Ask questions about specific episodes
- Search across multiple transcripts at once
- Get answers with exact citations and timestamps
- Follow up for deeper exploration

Example Questions:
- "What did Sam say about fundraising?"
- "Summarize the main points from this episode"
- "Compare Jason and Chamath's views on AI"
- "Find all mentions of productivity"

How Citations Work:
Answers include numbered references like [1], [2]. Click any citation to:
- See the full surrounding context
- Jump to the exact timestamp
- Open the full transcript`
      },
      {
        id: "chat-models",
        title: "Chat AI Models",
        content: `Choose which AI model powers your chat experience.

Free Models (All Users):
- Claude Sonnet 4 — Default, balanced performance
- GPT-4o Mini — Fast OpenAI model
- Gemini 2.5 Flash — Google's efficient model

Premium Models (BYOK Required):
- Claude Opus 4 — Most capable, best for complex analysis
- GPT-4o — OpenAI's flagship model
- Gemini Pro — Google's advanced model

How to Switch Models:
1. Open Chat
2. Click the model selector (CPU icon) next to the input
3. Choose your preferred model

What is BYOK?
Bring Your Own Key — add your API key from OpenAI, Anthropic, or Google in Settings to unlock premium models and unlimited chat.`
      },
      {
        id: "chat-limits",
        title: "Chat Message Limits",
        content: `Chat messages are included with your plan.

Monthly Limits by Tier:
| Tier | Messages/Month |
|------|----------------|
| PAYG | 20 |
| Base | 100 |
| Pro | Unlimited |
| Studio | Unlimited |
| Elite | Unlimited |

BYOK Bonus:
Add your own API key to get unlimited chat on any tier — including PAYG!

When Limits Reset:
Limits reset on your billing date each month. Unused messages don't roll over.

Approaching Your Limit?
- Consider upgrading to Pro for unlimited
- Add a BYOK key for instant unlimited access
- Wait for next month's reset`
      },
    ],
  },
  {
    id: "pricing",
    name: "Pricing & Billing",
    description: "Plans, payments, and billing",
    icon: <CreditCard className="h-6 w-6" />,
    articles: [
      {
        id: "pricing-tiers",
        title: "Pricing Tiers Explained",
        content: `Echonour offers flexible pricing from occasional users to power transcribers.

PAYG (Pay As You Go)
- No subscription
- $0.90/hr transcription
- 20 chat messages/month
- Top up anytime

Base — $15/month
- 20 hours/month
- $0.60/hr additional
- 100 chat messages/month
- Rollover: up to 40 hrs

Pro — $30/month ⭐ Most Popular
- 45 hours/month
- $0.52/hr additional
- Unlimited chat
- Rollover: up to 90 hrs

Studio — $55/month
- 90 hours/month
- $0.48/hr additional
- Unlimited chat
- Rollover: up to 180 hrs

Elite — $90/month
- 150 hours/month
- $0.44/hr additional
- Unlimited chat
- Rollover: up to 300 hrs

All tiers include:
- BYOK support
- All export formats
- Unlimited library storage`
      },
      {
        id: "annual-billing",
        title: "Annual Billing",
        content: `Save 15% with annual billing and get all hours upfront.

Annual Pricing:
| Tier | Monthly | Annual | Savings |
|------|---------|--------|---------|
| Base | $15 | $153/yr | $27 |
| Pro | $30 | $306/yr | $54 |
| Studio | $55 | $561/yr | $99 |
| Elite | $90 | $918/yr | $162 |

Key Difference: Hours Upfront
Unlike monthly plans, annual subscribers get ALL hours immediately:
- Pro Annual: 540 hours (45 × 12)
- Studio Annual: 1,080 hours
- Elite Annual: 1,800 hours

No Monthly Drip
Use your hours however you want throughout the year. No waiting for monthly allocations.

Rollover Not Needed
Since you have all hours upfront, there's no rollover mechanic — just one balance that depletes until renewal.`
      },
      {
        id: "rollover",
        title: "Hour Rollover",
        content: `Unused hours carry over to the next month (monthly plans only).

How It Works:
- Unused hours roll into next month
- Each tier has a rollover cap
- Hours above the cap expire

Rollover Caps:
| Tier | Cap |
|------|-----|
| Base | 40 hrs |
| Pro | 90 hrs |
| Studio | 180 hrs |
| Elite | 300 hrs |

Example:
Pro user starts with 45 hrs, uses 30:
- 15 hrs roll over
- Next month: 45 + 15 = 60 hrs available
- Cap is 90, so nothing expires

Note:
Annual subscribers don't need rollover — all hours are credited upfront and last until renewal.`
      },
      {
        id: "refunds",
        title: "Refund Policy",
        content: `Payments are processed by Paddle, our Merchant of Record.

14-Day Cancellation Right
If you're a consumer, you can cancel within 14 days for a full refund — unless you've already used the service.

After 14 Days
Refunds are provided at Paddle's discretion on a case-by-case basis.

Failed Transcriptions
If a transcription fails due to our error (not source unavailability), you won't be charged.

How to Request:
1. Contact Paddle directly through your purchase receipt
2. Or email support@echonour.com and we'll help

Subscription Cancellation
Cancel anytime from your account. You'll keep access until the end of your billing period.`
      },
      {
        id: "cost-calculator",
        title: "Cost Calculator",
        content: `Compare transcription costs across services with our interactive calculator.

[Open Cost Calculator →](/tools/cost-calculator)

Enter your monthly audio hours and see exactly what you'll pay with:
- EchoNour
- Otter.ai
- Descript
- Rev

Why Compare?
Different services have different pricing models:
- EchoNour: Pay-as-you-go ($0.10/min after free tier)
- Competitors: Monthly subscriptions with included hours

The calculator shows which option is most cost-effective for your usage level.

Note: Competitor pricing is verified regularly but may change. Calculator shows comparable plan tiers for content creators.`
      },
      {
        id: "comparisons",
        title: "How We Compare to Other Tools",
        content: `See how EchoNour stacks up against popular transcription services.

Detailed Comparisons:
- [EchoNour vs Otter.ai →](/blog/echonour-vs-otter) — Best for live meetings vs podcast transcription
- [EchoNour vs Descript →](/blog/echonour-vs-descript) — Full editor vs focused transcription
- [EchoNour vs Rev →](/blog/echonour-vs-rev) — AI speed vs human accuracy
- [EchoNour vs Turboscribe →](/blog/echonour-vs-turboscribe) — Feature and pricing breakdown

EchoNour's Key Differences:
- Speaker names — We identify actual names (e.g., "Joe Rogan"), not generic labels
- YouTube/RSS support — Paste URLs directly, no download needed
- AI summaries — Every transcript includes an AI-generated summary
- Pay-as-you-go — No subscription lock-in, pay only for what you use
- Chat with transcripts — Ask questions and get cited answers

Try the [Cost Calculator](/tools/cost-calculator) to see which service is most economical for your usage.`
      },
    ],
  },
  {
    id: "byok",
    name: "Bring Your Own Key",
    description: "Use your own AI provider keys",
    icon: <Key className="h-6 w-6" />,
    articles: [
      {
        id: "byok-overview",
        title: "What is BYOK?",
        content: `BYOK (Bring Your Own Key) lets you use your own API keys for Chat.

Benefits:
1. Unlimited chat messages — No monthly limits
2. Premium models — Access Opus, GPT-4o, Gemini Pro
3. Direct billing — Pay providers at their rates
4. Works on any tier — Even PAYG gets unlimited chat

Supported Providers:
- Anthropic (Claude models)
- OpenAI (GPT models)
- Google (Gemini models)

How It Works:
1. Get an API key from your preferred provider
2. Add it in Settings > API Keys
3. Select premium models in Chat
4. Charges go directly to your provider account`
      },
      {
        id: "byok-setup",
        title: "Setting Up BYOK",
        content: `Add your API keys to unlock premium features.

Getting Your API Keys:

Anthropic (Claude):
1. Go to console.anthropic.com
2. Create account and add billing
3. API Keys → Create Key
4. Copy the key

OpenAI (GPT):
1. Go to platform.openai.com
2. Create account and add billing
3. API Keys → Create new secret key
4. Copy the key

Google (Gemini):
1. Go to makersuite.google.com
2. Sign in with Google
3. Get API Key
4. Copy the key

Adding to Echonour:
1. Go to Settings (top-right menu)
2. Scroll to API Keys section
3. Paste your key in the appropriate field
4. Click Save

Your key is encrypted immediately. We only show the last 4 characters.`
      },
      {
        id: "byok-security",
        title: "BYOK Security",
        content: `Your API keys are protected with enterprise-grade security.

Encryption:
- Keys encrypted with AES-256-GCM
- Industry-standard encryption
- Encrypted before storage

Access:
- Keys decrypted only at moment of use
- Never logged in plain text
- Only last 4 characters displayed

Deletion:
- Delete your keys anytime from Settings
- Immediate effect
- Falls back to platform models

Best Practices:
- Use API keys with billing limits set
- Rotate keys periodically
- Monitor your provider dashboard for usage`
      },
    ],
  },
  {
    id: "account",
    name: "Account & Settings",
    description: "Manage your account and preferences",
    icon: <Settings className="h-6 w-6" />,
    articles: [
      {
        id: "settings-overview",
        title: "Settings Overview",
        content: `Access Settings from the menu in the top-right corner.

Available Settings:

Language Preferences
- Auto-detect (default)
- Force specific language
- 99+ languages supported

API Keys (BYOK)
- Add Anthropic, OpenAI, or Google keys
- Unlock unlimited chat
- Access premium models

Privacy
- Training opt-out toggle
- Data retention info

Account
- View email and plan
- Manage subscription
- Delete account`
      },
      {
        id: "privacy-settings",
        title: "Privacy Settings",
        content: `Control how your data is used.

Training Opt-Out
By default, anonymized chat data may be used to improve our service. You can opt out:
1. Go to Settings
2. Find "Training Opt-Out"
3. Toggle it on

When opted out:
- Your chat data won't be used for training
- Transcripts are never used for training (always private)

Data Storage:
- Transcripts: Stored until you delete them
- Chat history: Stored until you delete it
- Backups: Retained for 7 days

Your Rights:
- Access: View all your data in Library
- Export: Download transcripts anytime
- Delete: Remove individual items or full account`
      },
      {
        id: "delete-account",
        title: "Deleting Your Account",
        content: `You can delete your account and all associated data.

What Gets Deleted:
- All transcripts in your Library
- Chat history and sessions
- Folders and organization
- API keys and settings
- Usage history

What We Keep:
- Payment records (required by law)
- Anonymized usage statistics

How to Delete:
1. Email support@echonour.com
2. Include your account email
3. We'll process within 30 days

Before Deleting:
- Export any transcripts you want to keep
- Cancel any active subscription
- Remove BYOK API keys`
      },
    ],
  },
];

// Promoted/Featured articles
const PROMOTED_ARTICLES = [
  { id: "what-is-echonour", title: "What is Echonour?", category: "getting-started" },
  { id: "first-transcription", title: "Your First Transcription", category: "getting-started" },
  { id: "pricing-tiers", title: "Pricing Tiers Explained", category: "pricing" },
  { id: "chat-overview", title: "How to Use Chat", category: "chat" },
  { id: "byok-setup", title: "Setting Up BYOK", category: "byok" },
  { id: "speaker-identification", title: "Speaker Identification", category: "transcription" },
];

export default function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  // Search across all articles
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const results: { article: Article; category: Category }[] = [];
    
    CATEGORIES.forEach((cat) => {
      cat.articles.forEach((article) => {
        if (
          article.title.toLowerCase().includes(q) ||
          article.content.toLowerCase().includes(q)
        ) {
          results.push({ article, category: cat });
        }
      });
    });
    
    return results;
  }, [searchQuery]);

  const currentCategory = selectedCategory 
    ? CATEGORIES.find((c) => c.id === selectedCategory) 
    : null;

  // Simple markdown renderer
  // Parse inline links [text](url) into React elements
  const parseLinks = (text: string, keyPrefix: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;
    let linkIndex = 0;
    
    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const [, linkText, url] = match;
      parts.push(
        <Link 
          key={`${keyPrefix}-link-${linkIndex++}`} 
          to={url} 
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {linkText}
        </Link>
      );
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  const renderMarkdown = (content: string) => {
    return content.split("\n").map((line, i) => {
      // Headers
      if (line.startsWith("") && line.endsWith("")) {
        return <h3 key={i} className="font-semibold text-gray-900 mt-4 mb-2">{line.slice(2, -2)}</h3>;
      }
      // Bold sections with possible links
      if (line.startsWith("**")) {
        const parts = line.split("**");
        return (
          <p key={i} className="mb-2">
            {parts.map((part, j) => 
              j % 2 === 1 ? <strong key={j}>{parseLinks(part, `${i}-${j}`)}</strong> : parseLinks(part, `${i}-${j}`)
            )}
          </p>
        );
      }
      // List items with links
      if (line.startsWith("- ")) {
        return <li key={i} className="ml-4 mb-1 text-gray-700">{parseLinks(line.slice(2), `li-${i}`)}</li>;
      }
      // Table rows
      if (line.startsWith("|")) {
        const cells = line.split("|").filter(Boolean).map(c => c.trim());
        if (line.includes("---")) return null;
        return (
          <tr key={i} className="border-b border-gray-200">
            {cells.map((cell, j) => (
              <td key={j} className="px-3 py-2 text-sm">{parseLinks(cell, `td-${i}-${j}`)}</td>
            ))}
          </tr>
        );
      }
      // Code
      if (line.startsWith("`") && line.endsWith("`")) {
        return <code key={i} className="block bg-gray-100 px-3 py-2 rounded text-sm font-mono my-2">{line.slice(1, -1)}</code>;
      }
      // Empty line
      if (!line.trim()) return <div key={i} className="h-2" />;
      // Regular paragraph with links
      return <p key={i} className="text-gray-700 mb-2">{parseLinks(line, `p-${i}`)}</p>;
    });
  };

  // Render article detail view
  if (selectedArticle) {
    return (
      <div className="min-h-[calc(100vh-68px)] bg-gray-50">
        <div className="mx-auto max-w-3xl px-4 py-8">
          <button
            onClick={() => setSelectedArticle(null)}
            className="mb-6 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
          >
            ← Back to {currentCategory?.name || "Help Center"}
          </button>
          
          <article className="bg-white rounded-lg border border-gray-200 p-6 md:p-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">{selectedArticle.title}</h1>
            <div className="prose prose-gray max-w-none">
              {renderMarkdown(selectedArticle.content)}
            </div>
          </article>

          {/* Related articles */}
          {currentCategory && (
            <div className="mt-8">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                More in {currentCategory.name}
              </h3>
              <div className="space-y-2">
                {currentCategory.articles
                  .filter((a) => a.id !== selectedArticle.id)
                  .slice(0, 3)
                  .map((article) => (
                    <button
                      key={article.id}
                      onClick={() => setSelectedArticle(article)}
                      className="flex items-center justify-between w-full p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 text-left transition-colors"
                    >
                      <span className="text-gray-900">{article.title}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render category view
  if (selectedCategory && currentCategory) {
    return (
      <div className="min-h-[calc(100vh-68px)] bg-gray-50">
        <div className="mx-auto max-w-3xl px-4 py-8">
          <button
            onClick={() => setSelectedCategory(null)}
            className="mb-6 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
          >
            ← Back to Help Center
          </button>
          
          <div className="flex items-center gap-4 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              {currentCategory.icon}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{currentCategory.name}</h1>
              <p className="text-gray-500">{currentCategory.description}</p>
            </div>
          </div>

          <div className="space-y-3">
            {currentCategory.articles.map((article) => (
              <button
                key={article.id}
                onClick={() => setSelectedArticle(article)}
                className="flex items-center justify-between w-full p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm text-left transition-all"
              >
                <span className="font-medium text-gray-900">{article.title}</span>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main help center view
  return (
    <div className="min-h-[calc(100vh-68px)] bg-gray-50">
      {/* Hero with search */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            How can we help you?
          </h1>
          <p className="text-blue-100 mb-8">
            Search our help center or browse categories below
          </p>
          
          {/* Search bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for articles..."
              className="w-full rounded-xl border-0 bg-white py-4 pl-12 pr-4 text-gray-900 placeholder:text-gray-400 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          {/* Search results dropdown */}
          {searchQuery && searchResults.length > 0 && (
            <div className="relative max-w-2xl mx-auto mt-2">
              <div className="absolute top-0 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-200 max-h-80 overflow-y-auto z-10">
                {searchResults.slice(0, 8).map(({ article, category }) => (
                  <button
                    key={article.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setSelectedArticle(article);
                      setSearchQuery("");
                    }}
                    className="flex items-center gap-3 w-full p-4 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    <div className="text-blue-600">{category.icon}</div>
                    <div>
                      <div className="font-medium text-gray-900">{article.title}</div>
                      <div className="text-sm text-gray-500">{category.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {searchQuery && searchResults.length === 0 && (
            <div className="mt-4 text-blue-200">
              No results found for "{searchQuery}"
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-12">
        {/* Promoted Articles */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Popular Articles
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {PROMOTED_ARTICLES.map((promo) => {
              const category = CATEGORIES.find((c) => c.id === promo.category);
              const article = category?.articles.find((a) => a.id === promo.id);
              if (!category || !article) return null;
              
              return (
                <button
                  key={promo.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setSelectedArticle(article);
                  }}
                  className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm text-left transition-all"
                >
                  <div className="text-blue-600 shrink-0">{category.icon}</div>
                  <span className="font-medium text-gray-900">{promo.title}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Categories Grid */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Browse by Category
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className="flex flex-col items-start p-6 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md text-left transition-all group"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600 mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  {category.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{category.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{category.description}</p>
                <span className="text-sm text-blue-600 flex items-center gap-1">
                  {category.articles.length} articles
                  <ChevronRight className="h-4 w-4" />
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Blog / Learn More section */}
        <section className="mt-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 p-8">
          <div className="flex items-start gap-4">
            <BookOpen className="h-8 w-8 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Want to learn more?
              </h3>
              <p className="text-gray-600 mb-4">
                Check out our blog for in-depth guides on transcription, speaker identification, and getting the most out of Echonour.
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-blue-600 font-medium hover:text-blue-700"
              >
                Read the blog
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Contact section */}
        <section className="mt-8 rounded-xl bg-white border border-gray-200 p-8 text-center">
          <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Still need help?
          </h3>
          <p className="text-gray-500 mb-6">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <a
            href="mailto:support@echonour.com"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            Contact Support
          </a>
        </section>

        {/* Footer links */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm">
          <Link to="/blog" className="text-gray-500 hover:text-gray-700">
            Blog
          </Link>
          <Link to="/privacy" className="text-gray-500 hover:text-gray-700">
            Privacy Policy
          </Link>
          <Link to="/terms" className="text-gray-500 hover:text-gray-700">
            Terms of Service
          </Link>
          <Link to="/pricing" className="text-gray-500 hover:text-gray-700">
            Pricing
          </Link>
        </div>
      </div>
    </div>
  );
}
